import React, { ReactNode, useMemo } from 'react';
import { BaseCard } from '@/components/uiKit/Cards/BaseCard';
import { ComponentCard } from './ComponentCard';
import { useUIKitFilter } from '@/components/uiKit/_hooks/useUIKitFilter';
import { Counter } from '@/components/uiKit/Counter/Counter';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { ChevronRight, Lightbulb, CheckSquare } from 'lucide-react';
import { UIUnitsAccordion } from './UIUnitsAccordion';
import { MaintenanceCard } from './MaintenanceCard';
import { Varinat } from '@/types/shared/component-selector-variant.types';
import { generateTestId } from '@/utils/shared/generateTestIds';
import { ObservationsDrawer, VisibilityConfig } from './ObservationsDrawer';
import { Button } from '@/components/ui/button';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';

// Re-export VisibilityConfig for external use
export type { VisibilityConfig };

interface ComponentCardFilterProps {
  components: any[];
  defaultSelected?: Record<string, string[]>;
  multiSelect: boolean;
  onFilterChange?: (filters: any) => void;
  allItemsLable?: string | ReactNode;
  renderAllItemsCard?: () => ReactNode;
  renderItemsCard?: (item: any) => ReactNode;
  isAccordion?: boolean; // New prop to enable/disable accordion functionality
  uiUnits?: any[];
  onClick?: (el: any) => void;
  groupKey?: string;
  variant?: Varinat;
  visibilityConfig?: VisibilityConfig;
}
export const ComponentsCardFilter = ({
  allItemsLable = 'Show All ',
  components,
  defaultSelected,
  multiSelect,
  onFilterChange,
  renderAllItemsCard,
  renderItemsCard,
  isAccordion = false, // Default to false
  uiUnits = [],
  onClick,
  groupKey,
  variant,
  visibilityConfig = { observations: true, actions: false },
}: ComponentCardFilterProps) => {
  const { filterData, allItemsSelected, toggleAll, areAllSelected, isSelected, toggleItem } = useUIKitFilter({
    data: components,
    defaultSelected,
    multiSelect,
    type: 'filter',
    onFilterChange,
  });

  const totalCounter = filterData?.reduce(
    (accumulator, currentValue) => accumulator + currentValue?.menuItems?.length,
    0
  );

  const { selectedDiagnostic } = useSelector((state: RootState) => state.assetsStudio);

  // Calculate total observations and actions across all components
  const { totalObservationsCount, allDiagnostics, allEvidencesText, allActionsDescriptions, assetHealthStatus } =
    useMemo(() => {
      const allComponents = selectedDiagnostic?.[1]?.assets?.[0]?.components || [];
      const assetHealth = selectedDiagnostic?.[1]?.assets?.[0]?.health_status || 'na';

      let total = 0;
      const diagnostics: any[] = [];
      const evidences: string[] = [];
      const actions: string[] = [];

      allComponents.forEach((component: any) => {
        // Check for evidences_text array
        const evidencesText = component?.evidences_text || [];
        const actionsDescriptions = component?.actions_descriptions || [];

        if (evidencesText.length > 0) {
          total += evidencesText.length;
          evidences.push(...evidencesText);
        } else {
          // Fallback: count from diagnostics.sources_info
          const componentDiagnostics = component?.diagnostics || [];

          componentDiagnostics.forEach((diagnostic: any) => {
            const count = (diagnostic.sources_info || []).filter(
              (source: any) => source.evidence_text && source.evidence_text.trim()
            ).length;
            total += count;
          });
        }

        // Collect actions
        if (actionsDescriptions.length > 0) {
          actions.push(...actionsDescriptions);
        }

        // Collect all diagnostics with component context
        if (component?.diagnostics) {
          // Attach component name to each diagnostic for Asset Overview display
          const diagnosticsWithComponent = component.diagnostics.map((diag: any) => ({
            ...diag,
            component_name: component.component_name || component.component_ui_name,
            component_ui_name: component.component_ui_name || component.component_name,
          }));
          diagnostics.push(...diagnosticsWithComponent);
        }
      });

      return {
        totalObservationsCount: total,
        allDiagnostics: diagnostics,
        allEvidencesText: evidences,
        allActionsDescriptions: actions,
        assetHealthStatus: assetHealth,
      };
    }, [selectedDiagnostic, isAccordion, multiSelect]);

  if (!filterData) return null;
  return (
    <div className="flex flex-col gap-2 h-full">
      {isAccordion && !multiSelect && (
        <BaseCard
          // onClick={() => toggleAll(group.menuLabel!, group.menuItems)}
          classNames={{
            card: `rounded-lg py-1 hover:ring-2 ring-primary-500 hover:cursor-pointer min-w-full ${
              allItemsSelected && 'ring-2'
            }`,
            content: 'font-medium flex justify-between w-full items-center py-1',
          }}
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex-1">{renderAllItemsCard ? renderAllItemsCard() : allItemsLable}</div>
            <div className="flex items-center gap-2">
              {visibilityConfig.observations && totalObservationsCount > 0 && (
                <ObservationsDrawer
                  componentName="Asset Overview"
                  diagnostics={allDiagnostics}
                  evidencesText={allEvidencesText}
                  actionsDescriptions={allActionsDescriptions}
                  componentHealthStatus={assetHealthStatus}
                  initialTab="observations"
                  visibilityConfig={visibilityConfig}
                  isAssetOverview={true}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 relative hover:bg-amber-50 dark:hover:bg-amber-950/20"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Lightbulb className="h-4 w-4 text-amber-500" />
                    <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-amber-500 text-[10px] font-semibold text-white flex items-center justify-center">
                      {totalObservationsCount}
                    </span>
                    <span className="sr-only">
                      View {totalObservationsCount} observation{totalObservationsCount !== 1 ? 's' : ''}
                    </span>
                  </Button>
                </ObservationsDrawer>
              )}
              {visibilityConfig.actions && allActionsDescriptions.length > 0 && (
                <ObservationsDrawer
                  componentName="Asset Overview"
                  diagnostics={allDiagnostics}
                  evidencesText={allEvidencesText}
                  actionsDescriptions={allActionsDescriptions}
                  componentHealthStatus={assetHealthStatus}
                  initialTab="actions"
                  visibilityConfig={visibilityConfig}
                  isAssetOverview={true}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 relative hover:bg-blue-50 dark:hover:bg-blue-950/20"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <CheckSquare className="h-4 w-4 text-blue-500" />
                    <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-blue-500 text-[10px] font-semibold text-white flex items-center justify-center">
                      {allActionsDescriptions.length}
                    </span>
                    <span className="sr-only">
                      View {allActionsDescriptions.length} action{allActionsDescriptions.length !== 1 ? 's' : ''}
                    </span>
                  </Button>
                </ObservationsDrawer>
              )}
              <Counter variant="tab" size="lg" value={totalCounter} />
            </div>
          </div>
        </BaseCard>
      )}
      {isAccordion ? (
        <Accordion type="single" collapsible>
          {filterData?.map((group) => {
            const subItemCounter = group?.menuItems?.length;
            return (
              <AccordionItem key={group.menuLabel} value={group.menuLabel!}>
                {multiSelect && (
                  <AccordionTrigger className="p-0 my-1 group [&[data-state=open]>svg]:opacity-0 [&[data-state=closed]>svg]:opacity-0 hover:no-underline">
                    <BaseCard
                      // onClick={() => toggleAll(group.menuLabel!, group.menuItems)}
                      classNames={{
                        card: `rounded-lg py-1 hover:ring-2 ring-primary-500 hover:cursor-pointer min-w-full
                        ${areAllSelected(group.menuLabel!, group.menuItems) && 'ring-2'}`,
                        // card: `rounded-lg py-1 hover:ring-2 ring-primary-500 hover:cursor-pointer min-w-full`,
                        content: 'font-medium flex justify-between w-full',
                      }}
                    >
                      <div className="flex items-center justify-between w-full pr-2">
                        <div> {group.menuLabel}</div>
                      </div>
                      <ChevronRight className="group-[&[data-state=open]]:rotate-90" />
                    </BaseCard>
                  </AccordionTrigger>
                )}

                <AccordionContent>
                  <div className="ml-4 mr-1 gap-2 flex flex-col mt-1 ">
                    {group?.menuItems?.map((component, index) => {
                      return renderItemsCard ? (
                        <React.Fragment key={component?.id || index}>{renderItemsCard(component)}</React.Fragment>
                      ) : (
                        <React.Fragment key={component?.id || index}>
                          <ComponentCard
                            item={component}
                            isSelected={isSelected(group.menuLabel!, component.id)}
                            visibilityConfig={visibilityConfig}
                            // onClick={() => toggleItem(group.menuLabel!, component)}
                            onClick={() => {
                              toggleItem(group.menuLabel!, component);
                              // Delay drawer opening to allow selected indicator to show first
                              if (onClick) {
                                requestAnimationFrame(() => {
                                  onClick(component);
                                });
                              }
                            }}
                          />
                        </React.Fragment>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      ) : (
        filterData.map((group) => {
          const totalCounter = group?.menuItems?.length;
          return (
            <React.Fragment key={group.menuLabel}>
              {multiSelect && (
                <BaseCard
                  classNames={{
                    card: `rounded-lg py-1 hover:ring-2 ring-primary-500 hover:cursor-pointer min-w-full ${
                      allItemsSelected && 'ring-2'
                    }`,
                    content: 'font-medium flex justify-between w-full items-center py-1',
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex-1">{renderAllItemsCard ? renderAllItemsCard() : allItemsLable}</div>
                    <div className="flex items-center gap-2">
                      {visibilityConfig.observations && totalObservationsCount > 0 && (
                        <ObservationsDrawer
                          componentName="Asset Overview"
                          diagnostics={allDiagnostics}
                          evidencesText={allEvidencesText}
                          actionsDescriptions={allActionsDescriptions}
                          componentHealthStatus={assetHealthStatus}
                          initialTab="observations"
                          visibilityConfig={visibilityConfig}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 relative hover:bg-amber-50 dark:hover:bg-amber-950/20"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <Lightbulb className="h-4 w-4 text-amber-500" />
                            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-amber-500 text-[10px] font-semibold text-white flex items-center justify-center">
                              {totalObservationsCount}
                            </span>
                            <span className="sr-only">
                              View {totalObservationsCount} observation{totalObservationsCount !== 1 ? 's' : ''}
                            </span>
                          </Button>
                        </ObservationsDrawer>
                      )}
                      {visibilityConfig.actions && allActionsDescriptions.length > 0 && (
                        <ObservationsDrawer
                          componentName="Asset Overview"
                          diagnostics={allDiagnostics}
                          evidencesText={allEvidencesText}
                          actionsDescriptions={allActionsDescriptions}
                          componentHealthStatus={assetHealthStatus}
                          initialTab="actions"
                          visibilityConfig={visibilityConfig}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 relative hover:bg-blue-50 dark:hover:bg-blue-950/20"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <CheckSquare className="h-4 w-4 text-blue-500" />
                            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-blue-500 text-[10px] font-semibold text-white flex items-center justify-center">
                              {allActionsDescriptions.length}
                            </span>
                            <span className="sr-only">
                              View {allActionsDescriptions.length} action
                              {allActionsDescriptions.length !== 1 ? 's' : ''}
                            </span>
                          </Button>
                        </ObservationsDrawer>
                      )}
                      <Counter variant="tab" size="lg" value={totalCounter} />
                    </div>
                  </div>
                </BaseCard>
              )}
              {multiSelect && (
                <BaseCard
                  onClick={() => toggleAll(group.menuLabel!, group.menuItems)}
                  classNames={{
                    card: `rounded-lg py-1 hover:ring-1 ring-primary-500 hover:cursor-pointer min-h-fit ${
                      areAllSelected(group.menuLabel!, group.menuItems) && 'ring-2'
                    }`,
                    content: 'font-medium flex justify-between w-full',
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex-1">
                      {renderAllItemsCard ? renderAllItemsCard() : `${allItemsLable} ${group.menuLabel || 'items'}`}
                    </div>
                    <div className="flex items-center gap-2">
                      <Counter variant="tab" size="lg" value={totalCounter} />
                    </div>
                  </div>
                </BaseCard>
              )}
              <div className="gap-2 flex flex-col ">
                {uiUnits?.length > 0 ? (
                  <UIUnitsAccordion
                    variant={variant}
                    group={{ menuLabel: group.menuLabel, menuItems: group.menuItems }}
                    groupKey={groupKey}
                    uiUnits={variant === 'base' ? uiUnits.sort() : uiUnits}
                    renderItemsCard={renderItemsCard}
                    isSelected={isSelected}
                    toggleItem={toggleItem}
                    onClick={onClick}
                  />
                ) : (
                  group?.menuItems?.map((component, index) => {
                    return (
                      <div key={component?.id || index}>
                        {renderItemsCard ? (
                          <BaseCard
                            onClick={() => {
                              toggleItem(group.menuLabel!, component);
                              // Delay drawer opening to allow selected indicator to show first
                              if (onClick) {
                                requestAnimationFrame(() => {
                                  onClick(component);
                                });
                              }
                            }}
                            classNames={{
                              card: `rounded-lg py-1 hover:ring-1 ring-primary-500 hover:cursor-pointer ${
                                isSelected(group.menuLabel!, component.id) && 'ring-2'
                              }`,
                              content: 'font-medium flex justify-between w-full',
                            }}
                          >
                            {renderItemsCard(component)}
                          </BaseCard>
                        ) : variant === 'maintenance' ? (
                          <MaintenanceCard
                            item={{
                              ...component,
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              test_id: `card_${generateTestId(component.component_ui_name || component.name)}`,
                            }}
                            isSelected={isSelected(group.menuLabel!, component.id)}
                            onClick={() => {
                              toggleItem(group.menuLabel!, component);
                              // Delay drawer opening to allow selected indicator to show first
                              if (onClick) {
                                requestAnimationFrame(() => {
                                  onClick(component);
                                });
                              }
                            }}
                          />
                        ) : (
                          <ComponentCard
                            item={component}
                            isSelected={isSelected(group.menuLabel!, component.id)}
                            visibilityConfig={visibilityConfig}
                            onClick={() => {
                              toggleItem(group.menuLabel!, component);
                              // Delay drawer opening to allow selected indicator to show first
                              if (onClick) {
                                requestAnimationFrame(() => {
                                  onClick(component);
                                });
                              }
                            }}
                          />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </React.Fragment>
          );
        })
      )}
    </div>
  );
};
